<script setup lang="ts">
import { Line } from 'vue-chartjs'
import {
  CategoryScale,
  Chart as ChartJS,
  type ChartDataset,
  type ChartOptions,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js'
import { useCabinetReports } from '@/pages/object/CabinetReports'
import { computed, onBeforeMount, watch } from 'vue'
import useModel from './../model/model'
import { VPreloader } from '@/shared/ui'
import { useHelpers, useSseConnection, useTranslate } from '@/shared/lib'
import { useRoute } from 'vue-router'

ChartJS.register(Title, Tooltip, Legend, LineElement, CategoryScale, LinearScale, PointElement)

const { toTimeFormat, numToKilo, addColorAlpha } = useHelpers()
const { loadTable, data } = useModel()
const { trs } = useTranslate()
const model = useCabinetReports()
const route = useRoute()

model.filter.per_page = 9999
const { refetch, isFetching } = loadTable(model.filter)

/** Устанавливаем данные для графика */
const chartOptions: ChartOptions<'line'> = {
  responsive: true,
}

/**
 * Генерация данных для графика
 */
const chartData = computed(() => {
  const labels: string[] = []
  const datasets: ChartDataset<'line'>[] = []

  const values = {
    a: { data: [], label: 'A+', color: 'oklch(57.7% 0.245 27.325)' },
    r: { data: [], label: 'R+', color: 'oklch(62.7% 0.194 149.214)' },
    a_1: {
      data: [],
      label: `A+ ${trs('itf.tariff_count', { count: 1 })}`,
      color: 'oklch(80.8% 0.114 19.571)',
    },
    a_2: {
      data: [],
      label: `A+ ${trs('itf.tariff_count', { count: 2 })}`,
      color: 'oklch(89.2% 0.058 10.001)',
    },
    a_3: {
      data: [],
      label: `A+ ${trs('itf.tariff_count', { count: 3 })}`,
      color: 'oklch(92.4% 0.12 95.746)',
    },
    a_4: {
      data: [],
      label: `A+ ${trs('itf.tariff_count', { count: 4 })}`,
      color: 'oklch(72.3% 0.219 149.579)',
    },
    r_1: {
      data: [],
      label: `R+ ${trs('itf.tariff_count', { count: 1 })}`,
      color: 'oklch(58.8% 0.158 241.966)',
    },
    r_2: {
      data: [],
      label: `R+ ${trs('itf.tariff_count', { count: 2 })}`,
      color: 'oklch(39.8% 0.195 277.366)',
    },
    r_3: {
      data: [],
      label: `R+ ${trs('itf.tariff_count', { count: 3 })}`,
      color: 'oklch(76.9% 0.188 70.08)',
    },
    r_4: {
      data: [],
      label: `R+ ${trs('itf.tariff_count', { count: 4 })}`,
      color: 'oklch(74% 0.238 322.16)',
    },
  }

  if (data.value) {
    data.value.forEach((item) => {
      labels.push(toTimeFormat(item.date, 'HH:mm dd.MM.yyyy'))

      for (let key in values) {
        values[key].data.push(numToKilo(item[key]))
      }
    })
  }

  for (let key in values) {
    datasets.push({
      label: values[key].label,
      data: values[key].data,
      borderColor: values[key].color,
      backgroundColor: addColorAlpha(values[key].color, 0.5),
    })
  }

  return { labels, datasets }
})

/* Устанавливаем SSE соединение */
const { handler } = useSseConnection(async () => refetch())

watch(
  () => route.params.object_id,
  () => {
    handler(`/cabinets/${route.params.object_id}/statuses`)
  },
  { immediate: true },
)

/** Сбрасывание данных при создании компонента */
onBeforeMount(() => {
  model.filter.page = 1
  model.setCallback(refetch)

  refetch()
})
</script>

<template>
  <VPreloader v-if="isFetching" :wrapper-height="500" />
  <Line
    v-else
    class="mt-2"
    style="height: 500px; width: 100%"
    :options="chartOptions"
    :data="chartData"
  />
</template>
